import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { estagioService, alunoService, empresaService, instituicaoService, seguradoraService, cursoService } from '@/services/api';
import { Estagio, Aluno, Empresa, Instituicao, Seguradora, Curso } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const estagioSchema = z.object({
  alunoId: z.number().min(1, 'Aluno é obrigatório'),
  empresaId: z.number().min(1, 'Empresa é obrigatória'),
  instituicaoId: z.number().min(1, 'Instituição é obrigatória'),
  cursoId: z.union([z.number(), z.string().transform(val => val === '' ? undefined : Number(val))]).optional(),
  tipo: z.enum(['Obrigatorio', 'NaoObrigatorio'], {
    required_error: 'Tipo é obrigatório',
  }),
  remunerado: z.boolean(),
  origemInstituicao: z.string().optional(),
  dataInicio: z.string().min(1, 'Data de início é obrigatória'),
  dataTermino: z.string().min(1, 'Data de término é obrigatória'),
  cargaHorariaSemanal: z.number().min(1).max(44).optional(),
  bolsaAuxilio: z.number().optional(),
  seguroApolice: z.string().optional(),
  seguradoraId: z.number().optional(),
  status: z.enum(['Ativo', 'Cancelado', 'Concluido'], {
    required_error: 'Status é obrigatório',
  }),
  dataAssinatura: z.string().optional(),
  dataCancelamento: z.string().optional(),
  motivoEncerramentoId: z.number().optional(),
  possuiResponsavelMenor: z.boolean(),
});

type EstagioFormData = Omit<Estagio, 'id' | 'createdAt' | 'updatedAt' | 'aluno' | 'empresa' | 'instituicao' | 'seguradora' | 'curso' | 'horarios' | 'relatorios'>;

export const NovaEstagioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEstagios } = useQueryInvalidation();

  // Estados para os selects com busca
  const [alunos, setAlunos] = useState<Aluno[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [instituicoes, setInstituicoes] = useState<Instituicao[]>([]);
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [seguradoras, setSeguradoras] = useState<Seguradora[]>([]);
  
  // Queries de busca
  const [alunoQuery, setAlunoQuery] = useState<string>("");
  const [empresaQuery, setEmpresaQuery] = useState<string>("");
  const [instituicaoQuery, setInstituicaoQuery] = useState<string>("");
  const [cursoQuery, setCursoQuery] = useState<string>("");
  const [seguradoraQuery, setSeguradoraQuery] = useState<string>("");
  
  // Estados de abertura dos dropdowns
  const [openAluno, setOpenAluno] = useState<boolean>(false);
  const [openEmpresa, setOpenEmpresa] = useState<boolean>(false);
  const [openInstituicao, setOpenInstituicao] = useState<boolean>(false);
  const [openCurso, setOpenCurso] = useState<boolean>(false);
  const [openSeguradora, setOpenSeguradora] = useState<boolean>(false);
  
  // Refs para fechamento dos dropdowns
  const alunoRef = useRef<HTMLDivElement | null>(null);
  const empresaRef = useRef<HTMLDivElement | null>(null);
  const instituicaoRef = useRef<HTMLDivElement | null>(null);
  const cursoRef = useRef<HTMLDivElement | null>(null);
  const seguradoraRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EstagioFormData>({
    resolver: zodResolver(estagioSchema),
    defaultValues: {
      remunerado: false,
      possuiResponsavelMenor: false,
      status: 'Ativo',
      tipo: 'Obrigatorio',
    },
  });

  // Buscar dados do estágio (se editando)
  const { data: estagioData, isLoading: loadingEstagio } = useQuery({
    queryKey: ['estagio', id],
    queryFn: () => estagioService.getById(Number(id)),
    enabled: isEditing,
  });

  // Buscar dados para os selects
  const { data: alunosData, isLoading: loadingAlunos } = useQuery({
    queryKey: ['alunos'],
    queryFn: () => alunoService.list({ page: 1, limit: 1000 }),
  });

  const { data: empresasData, isLoading: loadingEmpresas } = useQuery({
    queryKey: ['empresas'],
    queryFn: () => empresaService.list({ page: 1, limit: 1000 }),
  });

  const { data: instituicoesData, isLoading: loadingInstituicoes } = useQuery({
    queryKey: ['instituicoes'],
    queryFn: () => instituicaoService.list({ page: 1, limit: 1000 }),
  });

  const { data: seguradorasData, isLoading: loadingSeguradoras } = useQuery({
    queryKey: ['seguradoras'],
    queryFn: () => seguradoraService.list({ page: 1, limit: 1000 }),
  });

  const { data: cursosData, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: () => cursoService.list({ page: 1, limit: 1000 }),
  });

  // Carregar dados dos selects com busca
  useEffect(() => {
    if (alunosData?.data) setAlunos(alunosData.data);
  }, [alunosData]);

  useEffect(() => {
    if (empresasData?.data) setEmpresas(empresasData.data);
  }, [empresasData]);

  useEffect(() => {
    if (instituicoesData?.data) setInstituicoes(instituicoesData.data);
  }, [instituicoesData]);

  useEffect(() => {
    if (cursosData?.data) setCursos(cursosData.data);
  }, [cursosData]);

  useEffect(() => {
    if (seguradorasData?.data) setSeguradoras(seguradorasData.data);
  }, [seguradorasData]);

  // Filtros para os selects com busca
  const filteredAlunos = alunos.filter(a =>
    !alunoQuery || 
    a.nome.toLowerCase().includes(alunoQuery.toLowerCase()) ||
    (a.cpf && a.cpf.includes(alunoQuery))
  );

  const filteredEmpresas = empresas.filter(e =>
    !empresaQuery || e.nome.toLowerCase().includes(empresaQuery.toLowerCase())
  );

  const filteredInstituicoes = instituicoes.filter(i =>
    !instituicaoQuery || i.nome.toLowerCase().includes(instituicaoQuery.toLowerCase())
  );

  const filteredCursos = cursos.filter(c =>
    !cursoQuery || c.nome.toLowerCase().includes(cursoQuery.toLowerCase())
  );

  const filteredSeguradoras = seguradoras.filter(s =>
    !seguradoraQuery || s.nome.toLowerCase().includes(seguradoraQuery.toLowerCase())
  );

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: any) {
      const target = event.target;
      if (alunoRef.current && target && !alunoRef.current.contains(target)) {
        setOpenAluno(false);
      }
      if (empresaRef.current && target && !empresaRef.current.contains(target)) {
        setOpenEmpresa(false);
      }
      if (instituicaoRef.current && target && !instituicaoRef.current.contains(target)) {
        setOpenInstituicao(false);
      }
      if (cursoRef.current && target && !cursoRef.current.contains(target)) {
        setOpenCurso(false);
      }
      if (seguradoraRef.current && target && !seguradoraRef.current.contains(target)) {
        setOpenSeguradora(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createEstagioMutation = useMutation({
    mutationFn: estagioService.create,
    onSuccess: () => {
      invalidateEstagios();
      handleSuccess('Estágio criado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar estágio');
    },
  });

  const updateEstagioMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EstagioFormData }) =>
      estagioService.update(id, data),
    onSuccess: () => {
      invalidateEstagios();
      handleSuccess('Estágio atualizado com sucesso!');
      navigate('/estagios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar estágio');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (estagioData) {
      setValue('alunoId', estagioData.alunoId);
      setValue('empresaId', estagioData.empresaId);
      setValue('instituicaoId', estagioData.instituicaoId);
      setValue('cursoId', estagioData.cursoId);
      setValue('tipo', estagioData.tipo);
      setValue('remunerado', estagioData.remunerado);
      setValue('origemInstituicao', estagioData.origemInstituicao || '');
      setValue('dataInicio', estagioData.dataInicio.split('T')[0]);
      setValue('dataTermino', estagioData.dataTermino.split('T')[0]);
      setValue('cargaHorariaSemanal', estagioData.cargaHorariaSemanal || undefined);
      setValue('bolsaAuxilio', estagioData.bolsaAuxilio || undefined);
      setValue('seguroApolice', estagioData.seguroApolice || '');
      setValue('seguradoraId', estagioData.seguradoraId || undefined);
      setValue('status', estagioData.status);
      setValue('dataAssinatura', estagioData.dataAssinatura?.split('T')[0] || '');
      setValue('dataCancelamento', estagioData.dataCancelamento?.split('T')[0] || '');
      setValue('motivoEncerramentoId', estagioData.motivoEncerramentoId || undefined);
      setValue('possuiResponsavelMenor', estagioData.possuiResponsavelMenor);

      // Preencher valores iniciais dos selects com busca
      if (estagioData.aluno) {
        setAlunoQuery(estagioData.aluno.nome);
      }
      if (estagioData.empresa) {
        setEmpresaQuery(estagioData.empresa.nome);
      }
      if (estagioData.instituicao) {
        setInstituicaoQuery(estagioData.instituicao.nome);
      }
      if (estagioData.seguradora) {
        setSeguradoraQuery(estagioData.seguradora.nome);
      }
      
      // Para o curso, buscar na lista de cursos disponíveis pelo cursoId
      if (estagioData.cursoId && cursos.length > 0) {
        const cursoSelecionado = cursos.find(c => c.id === estagioData.cursoId);
        if (cursoSelecionado) {
          setCursoQuery(cursoSelecionado.nome + (cursoSelecionado.nivel ? ` (${cursoSelecionado.nivel})` : ''));
        }
      }
    }
  }, [estagioData, setValue, cursos]);

  const onSubmit = async (data: EstagioFormData) => {
    try {
      setIsSubmitting(true);
      
      // Converter datas para ISO string e tratar valores vazios
      const formData = {
        ...data,
        dataInicio: new Date(data.dataInicio).toISOString(),
        dataTermino: new Date(data.dataTermino).toISOString(),
        dataAssinatura: data.dataAssinatura ? new Date(data.dataAssinatura).toISOString() : undefined,
        dataCancelamento: data.dataCancelamento ? new Date(data.dataCancelamento).toISOString() : undefined,
        cursoId: data.cursoId || undefined, // Converter string vazia para undefined
      } as any;

      // Debug: Log dos dados que serão enviados
      console.log('Dados do formulário:', formData);

      if (isEditing) {
        await updateEstagioMutation.mutateAsync({ id: Number(id), data: formData });
      } else {
        await createEstagioMutation.mutateAsync(formData);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = loadingEstagio || loadingAlunos || loadingEmpresas || loadingInstituicoes || loadingSeguradoras || loadingCursos;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Estágio' : 'Novo Estágio'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing 
            ? 'Edite as informações do estágio' 
            : 'Cadastre um novo estágio no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados Básicos */}
        <Card>
          <CardHeader>
            <CardTitle>Dados Básicos</CardTitle>
            <CardDescription>
              Informações essenciais do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="alunoId">Aluno *</Label>
                <div className="relative" ref={alunoRef}>
                  <input
                    type="text"
                    placeholder="Selecione o aluno"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.alunoId ? 'border-error-500' : 'border-gray-300'
                  }`}
                    value={alunoQuery}
                    onChange={(e) => { 
                      setAlunoQuery(e.target.value); 
                      setOpenAluno(true); 
                    }}
                    onFocus={() => setOpenAluno(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenAluno(false); }}
                  />
                  {alunoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar aluno"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setAlunoQuery(""); 
                        setValue('alunoId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openAluno && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Alunos</div>
                      {filteredAlunos.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('alunoId', a.id);
                            setAlunoQuery(a.nome);
                            setOpenAluno(false); 
                          }}
                        >
                          {a.nome} {a.cpf && `(${a.cpf})`}
                        </button>
                      ))}
                      {filteredAlunos.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum aluno encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.alunoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.alunoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="empresaId">Empresa *</Label>
                <div className="relative" ref={empresaRef}>
                  <input
                    type="text"
                    placeholder="Selecione a empresa"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.empresaId ? 'border-error-500' : 'border-gray-300'
                  }`}
                    value={empresaQuery}
                    onChange={(e) => { 
                      setEmpresaQuery(e.target.value); 
                      setOpenEmpresa(true); 
                    }}
                    onFocus={() => setOpenEmpresa(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenEmpresa(false); }}
                  />
                  {empresaQuery && (
                    <button
                      type="button"
                      aria-label="Limpar empresa"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setEmpresaQuery(""); 
                        setValue('empresaId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openEmpresa && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Empresas</div>
                      {filteredEmpresas.map(e => (
                        <button
                          key={e.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('empresaId', e.id);
                            setEmpresaQuery(e.nome);
                            setOpenEmpresa(false); 
                          }}
                        >
                          {e.nome}
                        </button>
                      ))}
                      {filteredEmpresas.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma empresa encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.empresaId && (
                  <p className="mt-1 text-sm text-error-600">{errors.empresaId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="instituicaoId">Instituição *</Label>
                <div className="relative" ref={instituicaoRef}>
                  <input
                    type="text"
                    placeholder="Selecione a instituição"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.instituicaoId ? 'border-error-500' : 'border-gray-300'
                  }`}
                    value={instituicaoQuery}
                    onChange={(e) => { 
                      setInstituicaoQuery(e.target.value); 
                      setOpenInstituicao(true); 
                    }}
                    onFocus={() => setOpenInstituicao(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenInstituicao(false); }}
                  />
                  {instituicaoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar instituição"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setInstituicaoQuery(""); 
                        setValue('instituicaoId', 0);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openInstituicao && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Instituições</div>
                      {filteredInstituicoes.map(i => (
                        <button
                          key={i.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('instituicaoId', i.id);
                            setInstituicaoQuery(i.nome);
                            setOpenInstituicao(false); 
                          }}
                        >
                          {i.nome}
                        </button>
                      ))}
                      {filteredInstituicoes.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma instituição encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.instituicaoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.instituicaoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cursoId">Curso</Label>
                <div className="relative" ref={cursoRef}>
                  <input
                    type="text"
                    placeholder="Selecione o curso (opcional)"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.cursoId ? 'border-error-500' : 'border-gray-300'
                  }`}
                    value={cursoQuery}
                    onChange={(e) => { 
                      setCursoQuery(e.target.value); 
                      setOpenCurso(true); 
                    }}
                    onFocus={() => setOpenCurso(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenCurso(false); }}
                  />
                  {cursoQuery && (
                    <button
                      type="button"
                      aria-label="Limpar curso"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setCursoQuery(""); 
                        setValue('cursoId', undefined);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openCurso && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Cursos</div>
                      {filteredCursos.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('cursoId', c.id);
                            setCursoQuery(c.nome + (c.nivel ? ` (${c.nivel})` : ''));
                            setOpenCurso(false); 
                          }}
                        >
                          {c.nome} {c.nivel && `(${c.nivel})`}
                        </button>
                      ))}
                      {filteredCursos.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum curso encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.cursoId && (
                  <p className="mt-1 text-sm text-error-600">{errors.cursoId.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="tipo">Tipo *</Label>
                <select
                  id="tipo"
                  {...register('tipo')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.tipo ? 'border-error-500' : 'border-gray-300'
                  }`}
                >
                  <option value="Obrigatorio">Obrigatório</option>
                  <option value="NaoObrigatorio">Não Obrigatório</option>
                </select>
                {errors.tipo && (
                  <p className="mt-1 text-sm text-error-600">{errors.tipo.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  {...register('status')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.status ? 'border-error-500' : 'border-gray-300'
                  }`}
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Cancelado">Cancelado</option>
                  <option value="Concluido">Concluído</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-error-600">{errors.status.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="origemInstituicao">Origem da Instituição</Label>
                <Input
                  id="origemInstituicao"
                  placeholder="Ex: Curso de Ciência da Computação"
                  {...register('origemInstituicao')}
                  className={errors.origemInstituicao ? 'border-error-500' : ''}
                />
                {errors.origemInstituicao && (
                  <p className="mt-1 text-sm text-error-600">{errors.origemInstituicao.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remunerado"
                  {...register('remunerado')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="remunerado" className="ml-2">
                  Estágio remunerado
                </Label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="possuiResponsavelMenor"
                  {...register('possuiResponsavelMenor')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <Label htmlFor="possuiResponsavelMenor" className="ml-2">
                  Possui responsável
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datas */}
        <Card>
          <CardHeader>
            <CardTitle>Datas</CardTitle>
            <CardDescription>
              Período e datas importantes do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dataInicio">Data de Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  {...register('dataInicio')}
                  className={errors.dataInicio ? 'border-error-500' : ''}
                />
                {errors.dataInicio && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataInicio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataTermino">Data de Término *</Label>
                <Input
                  id="dataTermino"
                  type="date"
                  {...register('dataTermino')}
                  className={errors.dataTermino ? 'border-error-500' : ''}
                />
                {errors.dataTermino && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataTermino.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataAssinatura">Data de Assinatura</Label>
                <Input
                  id="dataAssinatura"
                  type="date"
                  {...register('dataAssinatura')}
                  className={errors.dataAssinatura ? 'border-error-500' : ''}
                />
                {errors.dataAssinatura && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataAssinatura.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="dataCancelamento">Data de Cancelamento</Label>
                <Input
                  id="dataCancelamento"
                  type="date"
                  {...register('dataCancelamento')}
                  className={errors.dataCancelamento ? 'border-error-500' : ''}
                />
                {errors.dataCancelamento && (
                  <p className="mt-1 text-sm text-error-600">{errors.dataCancelamento.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detalhes Adicionais */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes Adicionais</CardTitle>
            <CardDescription>
              Informações complementares do estágio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cargaHorariaSemanal">Carga Horária Semanal (horas)</Label>
                <Input
                  id="cargaHorariaSemanal"
                  type="number"
                  min="1"
                  max="44"
                  placeholder="Ex: 20"
                  {...register('cargaHorariaSemanal', { valueAsNumber: true })}
                  className={errors.cargaHorariaSemanal ? 'border-error-500' : ''}
                />
                {errors.cargaHorariaSemanal && (
                  <p className="mt-1 text-sm text-error-600">{errors.cargaHorariaSemanal.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="bolsaAuxilio">Bolsa Auxílio (R$)</Label>
                <Input
                  id="bolsaAuxilio"
                  type="number"
                  step="0.01"
                  placeholder="Ex: 800.00"
                  {...register('bolsaAuxilio', { valueAsNumber: true })}
                  className={errors.bolsaAuxilio ? 'border-error-500' : ''}
                />
                {errors.bolsaAuxilio && (
                  <p className="mt-1 text-sm text-error-600">{errors.bolsaAuxilio.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguroApolice">Apólice do Seguro</Label>
                <Input
                  id="seguroApolice"
                  placeholder="Ex: AP123456"
                  {...register('seguroApolice')}
                  className={errors.seguroApolice ? 'border-error-500' : ''}
                />
                {errors.seguroApolice && (
                  <p className="mt-1 text-sm text-error-600">{errors.seguroApolice.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="seguradoraId">Seguradora</Label>
                <div className="relative" ref={seguradoraRef}>
                  <input
                    type="text"
                    placeholder="Selecione a seguradora (opcional)"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.seguradoraId ? 'border-error-500' : 'border-gray-300'
                  }`}
                    value={seguradoraQuery}
                    onChange={(e) => { 
                      setSeguradoraQuery(e.target.value); 
                      setOpenSeguradora(true); 
                    }}
                    onFocus={() => setOpenSeguradora(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenSeguradora(false); }}
                  />
                  {seguradoraQuery && (
                    <button
                      type="button"
                      aria-label="Limpar seguradora"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setSeguradoraQuery(""); 
                        setValue('seguradoraId', undefined);
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openSeguradora && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Seguradoras</div>
                      {filteredSeguradoras.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('seguradoraId', s.id);
                            setSeguradoraQuery(s.nome);
                            setOpenSeguradora(false); 
                          }}
                        >
                          {s.nome}
                        </button>
                      ))}
                      {filteredSeguradoras.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhuma seguradora encontrada</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.seguradoraId && (
                  <p className="mt-1 text-sm text-error-600">{errors.seguradoraId.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/estagios')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Estágio' : 'Criar Estágio')}
          </Button>
        </div>
      </form>
    </div>
  );
};
